import React from 'react';
import {View} from 'react-native';

import Icon from '@/components/Icon/Icon';
import Space from '@/components/Space/Space';
import StaticImage from '@/components/StaticImage';
import Text from '@/components/Text';
import TouchableCard from '@/components/Touchable/TouchableCard';
import {useThemedStyles} from '@/theme';
import {ITrack} from '@/types/common';
import {ImageSize} from '@/types/media';
import {getTrackArtistsNames} from '@/utils/tracks';

import {styles, TRACK_CARD_HEIGHT} from './TrackCard.style';

interface IProps {
  track: ITrack;
  onPlay: () => void;
  onTogglePlay: () => void;
  isPlaying: boolean;
  isCurrentTrack: boolean;
  count?: number;
  showArtist?: boolean;
}

const TrackCardEmbed: React.FC<IProps> = ({
  track,
  onPlay,
  onTogglePlay,
  isPlaying,
  isCurrentTrack,
  count,
  showArtist = false,
}) => {
  const style = useThemedStyles(styles);

  const {title, lossyArtworkUrl} = track;

  const onTrackCardPress = () => {
    if (isCurrentTrack) {
      onTogglePlay();
    } else {
      onPlay();
    }
  };

  const textColor = (actionColorResolver: any) =>
    isCurrentTrack ? actionColorResolver('activeText') : actionColorResolver();

  return (
    <TouchableCard
      onPress={onTrackCardPress}
      highlight={isCurrentTrack}
      style={style.track}>
      {({actionColor}) => (
        <>
          {lossyArtworkUrl && (
            <View style={style.artworkContainer}>
              <StaticImage
                resizeWidth={ImageSize.thumbnail}
                source={{uri: lossyArtworkUrl}}
                style={style.artwork}
              />
            </View>
          )}
          <Space flex pr="xxs" pv="xxs" style={style.trackSpace}>
            <Space style={style.titleRow}>
              <Text
                weight="semibold"
                numberOfLines={1}
                color={textColor(actionColor)}>
                {title}
              </Text>
              {count && count > 1 && (
                <Space pl="xs">
                  <Text size="s" weight="light" color={textColor(actionColor)}>
                    (x{count})
                  </Text>
                </Space>
              )}
            </Space>
            {showArtist && (
              <Text size="xs" numberOfLines={1} color={textColor(actionColor)}>
                {getTrackArtistsNames(track)}
              </Text>
            )}
          </Space>
          <Space ml="xxs" />
          {isCurrentTrack ? (
            <View style={style.embedAction}>
              <Icon
                name={isPlaying ? 'control-pause' : 'control-play'}
                size={18}
                color={textColor(actionColor)}
              />
            </View>
          ) : (
            <View style={style.embedAction}>
              <Icon
                name="control-play"
                size={18}
                color={textColor(actionColor)}
              />
            </View>
          )}
        </>
      )}
    </TouchableCard>
  );
};

export const EMBED_TRACK_CARD_HEIGHT = TRACK_CARD_HEIGHT;
export default React.memo(TrackCardEmbed);
